import { useId } from 'react'
import type { AvatarPresetId } from '../storage'
import { PRESET_LABEL } from '../avatarPresets'

// Shared torso silhouette across every preset — only head/hair/accessory
// paths vary (see src/assets/files/README.md's "drawing rules").
const TORSO_PATH = 'M-27,38 C-27,14 -14,6 0,6 C14,6 27,14 27,38'

// Every preset draws the same way at every size. The old compact variant
// gated the tiny facial marks behind a `showDetails` flag for dense lists; on an eye
// test the detailed glyph reads fine down at list size, so the gate is gone.
function PresetMarkup({ preset }: { preset: AvatarPresetId }) {
  switch (preset) {
    case 'fan-1':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path d="M-12,8 Q0,17 12,8 L19,14 Q0,25 -19,14 Z" fill="currentColor" stroke="none" />
          <path d="M8,16 L15,13 L17,26 L10,29 Z" fill="currentColor" stroke="none" />
          <circle cx="-4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'fan-2':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-14.5,-9 A14.5,14.5 0 0 1 14.5,-9 C15.5,-3 15,4 13.5,10 C12,4 10.8,-1 10.25,-5 A11,11 0 1 0 -10.25,-5 C-10.8,-1 -12,4 -13.5,10 C-15,4 -15.5,-3 -14.5,-9 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-12,8 Q0,17 12,8 L19,14 Q0,25 -19,14 Z" fill="currentColor" stroke="none" />
          <path d="M8,16 L15,13 L17,26 L10,29 Z" fill="currentColor" stroke="none" />
          <circle cx="-4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'gaffer-1':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path d="M-14,-12 Q0,-31 14,-12" fill="none" />
          <rect x="-19.5" y="-16" width="7" height="11" rx="3" fill="currentColor" stroke="none" />
          <rect x="12.5" y="-16" width="7" height="11" rx="3" fill="currentColor" stroke="none" />
          <path d="M-16,-7 Q-12,-1 -5,-1" fill="none" />
          <circle cx="-3" cy="-1" r="2.5" fill="currentColor" stroke="none" />
          <circle cx="-4.5" cy="-11" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-11" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'gaffer-2':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M13,-9 C21,-6 21,3 15,10 C15,3 13,-2 10.5,-5 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-14,-12 Q0,-31 14,-12" fill="none" />
          <rect x="-19.5" y="-16" width="7" height="11" rx="3" fill="currentColor" stroke="none" />
          <rect x="12.5" y="-16" width="7" height="11" rx="3" fill="currentColor" stroke="none" />
          <path d="M-16,-7 Q-12,-1 -5,-1" fill="none" />
          <circle cx="-3" cy="-1" r="2.5" fill="currentColor" stroke="none" />
          <circle cx="-4.5" cy="-11" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-11" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'ultra-1':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-12,-12 Q0,-31 12,-12 Q25,-11 24,-5 Q16,-9 8,-9 Z"
            fill="currentColor"
            stroke="none"
          />
          <circle cx="-4.5" cy="-5" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-5" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'ultra-2':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-11.5,-12 C-14.5,-4 -14.5,5 -12.5,15 C-10.5,6 -10.5,-4 -10.5,-11 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M10,-10 C14,-3 14.5,6 12.5,15 C11,7 10,-1 9,-8 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M-12,-12 Q0,-31 12,-12 Q25,-11 24,-5 Q16,-9 8,-9 Z"
            fill="currentColor"
            stroke="none"
          />
          <circle cx="-4.5" cy="-5" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-5" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'pundit-1':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-11,-11 Q-10,-23 0,-22 Q10,-23 11,-11 Q0,-16 -11,-11 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-10,8 L0,15 L10,8" fill="none" />
          <path d="M0,11 L-4,17 L0,28 L4,17 Z" fill="currentColor" stroke="none" />
          <circle cx="-4.5" cy="-8" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-8" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'pundit-2':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-14.5,-9 A14.5,14.5 0 0 1 14.5,-9 C15.8,2 15,10 12.5,17 C11.2,10 10.5,2 10.25,-5 A11,11 0 1 0 -10.25,-5 C-10.5,2 -11.2,10 -12.5,17 C-15,10 -15.8,2 -14.5,-9 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-10,8 L0,15 L10,8" fill="none" />
          <path d="M0,11 L-4,17 L0,28 L4,17 Z" fill="currentColor" stroke="none" />
          <circle cx="-4.5" cy="-8" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-8" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'lifer':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-9.5,-14.5 A11,11 0 0 0 -6.3,0 C-14,-1 -19,-8 -9.5,-14.5 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M9.5,-14.5 A11,11 0 0 1 6.3,0 C14,-1 19,-8 9.5,-14.5 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M-5,-2 Q-2.5,-3.5 0,-2.5 Q2.5,-3.5 5,-2 Q2.5,0.5 0,-0.5 Q-2.5,0.5 -5,-2 Z"
            fill="currentColor"
            stroke="none"
          />
          <circle cx="-4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'nan':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-15,-9 A15,15 0 0 1 15,-9 C15.5,-2 14.5,4 12,8 C12,2 10.8,-2 10.25,-5 A11,11 0 1 0 -10.25,-5 C-10.8,-2 -12,2 -12,8 C-14.5,4 -15.5,-2 -15,-9 Z"
            fill="currentColor"
            stroke="none"
          />
          <circle cx="0" cy="-24" r="5.5" fill="currentColor" stroke="none" />
          <circle cx="-5.6" cy="-8" r="4.6" fill="none" />
          <circle cx="5.6" cy="-8" r="4.6" fill="none" />
          <path d="M-1,-8 L1,-8" fill="none" />
        </>
      )
    case 'alien':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-8.8,-14.2 C-4.6,-16.4 -1.6,-13.2 -2.8,-9.4 C-5.6,-6 -9.8,-9.6 -8.8,-14.2 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M8.8,-14.2 C4.6,-16.4 1.6,-13.2 2.8,-9.4 C5.6,-6 9.8,-9.6 8.8,-14.2 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-3,-2.2 L3,-2.2" fill="none" />
          <path
            d="M-9,20 C-5.4,16.6 5.4,16.6 9,20 C5.4,23.4 -5.4,23.4 -9,20 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-3.8,18 C-3.2,14.2 3.2,14.2 3.8,18 Z" fill="currentColor" stroke="none" />
        </>
      )
    case 'bird':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-1,-20 C-4.5,-26 -2.5,-30.5 2,-30.5 C-0.5,-26 2,-23 3,-19.6 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-4,-6 L4,-6 L0,1.5 Z" fill="currentColor" stroke="none" />
          <circle cx="-5.5" cy="-11" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="5.5" cy="-11" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'devil':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-5.5,-18.6 C-9,-24 -13,-27.5 -16,-27 C-13.8,-21.5 -12,-16.5 -10.4,-12.6 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M5.5,-18.6 C9,-24 13,-27.5 16,-27 C13.8,-21.5 12,-16.5 10.4,-12.6 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-3.2,0.2 Q0,3.2 3.2,0.2 L0,8 Z" fill="currentColor" stroke="none" />
          <circle cx="-4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'dinosaur':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path d="M-9.5,-14.5 L-4.5,-19 L-7,-25.5 Z" fill="currentColor" stroke="none" />
          <path d="M-2,-19.8 L2,-19.8 L0,-27.5 Z" fill="currentColor" stroke="none" />
          <path d="M9.5,-14.5 L4.5,-19 L7,-25.5 Z" fill="currentColor" stroke="none" />
          <path
            d="M-7.5,-3.4 L7.5,-3.4 L5.6,1 L3.6,-2.6 L1.6,1 L-1.6,-2.6 L-3.6,1 L-5.6,-2.6 Z"
            fill="currentColor"
            stroke="none"
          />
          <circle cx="-5" cy="-11.5" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="5" cy="-11.5" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'eagle':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-10.4,-12.6 C-16,-16 -19,-21 -18,-24.5 C-14.5,-20.5 -12,-17.5 -9.2,-15 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M10.4,-12.6 C16,-16 19,-21 18,-24.5 C14.5,-20.5 12,-17.5 9.2,-15 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-9.2,-14.6 L-2.2,-11.2 L-2.2,-8.6 L-9.2,-11.8 Z" fill="currentColor" stroke="none" />
          <path d="M9.2,-14.6 L2.2,-11.2 L2.2,-8.6 L9.2,-11.8 Z" fill="currentColor" stroke="none" />
          <path
            d="M-3.6,-4.2 L3.6,-4.2 L1.8,2.4 C1,4.8 -1.4,4.2 -0.7,1.8 Z"
            fill="currentColor"
            stroke="none"
          />
          <circle cx="-5.5" cy="-6.2" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="5.5" cy="-6.2" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'elephant':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path
            d="M-8.5,-16 C-21,-19.5 -25,-6 -14,-0.5 C-11.5,-3 -11,-6 -11,-9 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M8.5,-16 C21,-19.5 25,-6 14,-0.5 C11.5,-3 11,-6 11,-9 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M-3,-4 C-3,3 -4.6,9 -3,13.5 C-2,16.6 1.2,17 2.4,15.4 C1.2,14 -0.6,13.5 -0.6,11 C-0.6,7 1.2,2 1.2,-4 Z"
            fill="currentColor"
            stroke="none"
          />
          <circle cx="-6.2" cy="-10.4" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="6.2" cy="-10.4" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'giraffe':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path d="M-4.2,-19.2 L-6.5,-25" fill="none" />
          <path d="M4.2,-19.2 L6.5,-25" fill="none" />
          <circle cx="-7" cy="-26.5" r="2.8" fill="currentColor" stroke="none" />
          <circle cx="7" cy="-26.5" r="2.8" fill="currentColor" stroke="none" />
          <path
            d="M-9.8,-14.2 C-15,-18 -18,-16 -16,-12 C-14,-9.5 -11.5,-11 -9.8,-12.2 Z"
            fill="currentColor"
            stroke="none"
          />
          <path
            d="M9.8,-14.2 C15,-18 18,-16 16,-12 C14,-9.5 11.5,-11 9.8,-12.2 Z"
            fill="currentColor"
            stroke="none"
          />
          <path d="M-4,-1.5 Q0,1.8 4,-1.5" fill="none" />
          <circle cx="-13" cy="17" r="3.4" fill="currentColor" stroke="none" />
          <circle cx="1" cy="23" r="3.9" fill="currentColor" stroke="none" />
          <circle cx="13" cy="16" r="3" fill="currentColor" stroke="none" />
          <circle cx="-4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'mouse':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <circle cx="-11" cy="-20" r="6.2" fill="currentColor" stroke="none" />
          <circle cx="11" cy="-20" r="6.2" fill="currentColor" stroke="none" />
          <path d="M-4,-3.4 L-15,-6" fill="none" />
          <path d="M-4,-2 L-15,-0.5" fill="none" />
          <path d="M4,-3.4 L15,-6" fill="none" />
          <path d="M4,-2 L15,-0.5" fill="none" />
          <circle cx="0" cy="-2.6" r="2.2" fill="currentColor" stroke="none" />
          <circle cx="-4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="-10" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'robot':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <path d="M0,-20 L0,-25.5" fill="none" />
          <circle cx="0" cy="-27.4" r="2.6" fill="currentColor" stroke="none" />
          <rect x="-8.2" y="-14.6" width="16.4" height="8.4" rx="3" fill="none" />
          <path d="M-4.2,-3 L-4.2,-0.2" fill="none" />
          <path d="M0,-3 L0,-0.2" fill="none" />
          <path d="M4.2,-3 L4.2,-0.2" fill="none" />
          <circle cx="-4.2" cy="-10.4" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.2" cy="-10.4" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
    case 'tiger':
      return (
        <>
          <circle cx="0" cy="-9" r="11" fill="none" />
          <circle cx="-9.8" cy="-17.8" r="4.6" fill="currentColor" stroke="none" />
          <circle cx="9.8" cy="-17.8" r="4.6" fill="currentColor" stroke="none" />
          <path d="M-6.4,-17.6 L-5.8,-13.4" fill="none" />
          <path d="M0,-19.8 L0,-15.4" fill="none" />
          <path d="M6.4,-17.6 L5.8,-13.4" fill="none" />
          <path d="M-11,-7.6 L-6.6,-8" fill="none" />
          <path d="M11,-7.6 L6.6,-8" fill="none" />
          <path d="M-2.6,-4.4 L2.6,-4.4 L0,-1.4 Z" fill="currentColor" stroke="none" />
          <path d="M0,-1.4 L0,0.6" fill="none" />
          <path d="M0,0.6 Q-3,2.6 -5.2,0.4" fill="none" />
          <path d="M0,0.6 Q3,2.6 5.2,0.4" fill="none" />
          <circle cx="-5" cy="-10.6" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="5" cy="-10.6" r="1.5" fill="currentColor" stroke="none" />
        </>
      )
  }
}

/** The doodle only, no badge chrome. Colour is inherited via `currentColor`
 * from an ancestor — pair with `--color-text` so it flips with theme. Clip
 * path id is generated per-instance so the same preset can render twice on
 * one page (e.g. picker + header) without collision — see
 * src/assets/files/README.md. */
export function AvatarGlyph({
  preset,
  className,
}: {
  preset: AvatarPresetId
  className?: string
}) {
  const clipId = useId()
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      role="img"
      aria-label={PRESET_LABEL[preset]}
      className={className}
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="32" cy="32" r="32" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <g
          transform="translate(32 32)"
          stroke="currentColor"
          strokeWidth={2.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={TORSO_PATH} fill="none" />
          <PresetMarkup preset={preset} />
        </g>
      </g>
    </svg>
  )
}

/** Badge with surface, ring, and initials fallback for when `preset` is
 * null — covers new accounts before their first pick. */
export function ProfileAvatar({
  preset,
  initials,
  className,
  onClick,
}: {
  preset: AvatarPresetId | null
  initials: string
  className?: string
  onClick?: () => void
}) {
  const content = preset ? (
    <AvatarGlyph preset={preset} className="profile-avatar__glyph" />
  ) : (
    initials
  )

  if (onClick) {
    return (
      <button
        type="button"
        className={`profile-avatar ${className ?? ''}`}
        onClick={onClick}
        aria-label="Change profile picture"
      >
        {content}
      </button>
    )
  }

  return <div className={`profile-avatar ${className ?? ''}`}>{content}</div>
}
